import { FC, useCallback, useEffect, useRef } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAnalyticsApi } from '@shared-web-analytics/react/dist';

import FeedbackModal from '../../components/modals/feedbackModal';
import IssueModal from '../../components/modals/addIssueModal';
import { ModalContainer } from './ModalPortal.styles';
import IssueModalView from '../../components/modals/addIssueModal/IssueModalView';
import AddProvisioningModal from '../../components/modals/addProvisioningModal';
import AddQuestionModal from '../../components/modals/addQuestionModal/AddQuestionModal';
import EmployeeRoundingSettingsModal from '../../components/modals/employeeRoundingSettingsModal';
import FilterModal from '../../components/modals/filterModal/FilterModal';
import StoplightModal from '../../components/modals/stoplightModal';
import UnlistedEmployeeRoundModal from '../../components/modals/unlistedEmployeeRoundModal';
import UnlistedPatientRoundModal from '../../components/modals/unlistedPatientRoundModal';
import UserModal from '../../components/modals/userModal';

import { RootState } from '../../redux/store';
import { closeModal, resetEditQuestion } from '../../redux/actions';
import { logTaskClick } from '../../utils/analyticsHelpers';

const ModalPortal: FC<{
  modalData: any;
  modalState: boolean;
  modalType: string;
}> = ({ modalData, modalState, modalType }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const modalContainerRef = useRef<HTMLDivElement>(null);

  const storedDeepLinkTask =
    pathname.includes('task/complete') && Object.keys(modalData).length > 0;

  const { logTrackingEvent } = useAnalyticsApi();

  const closeFromOutside = useCallback(
    (e: Event) => {
      const target = e.target as HTMLElement;
      if (
        modalContainerRef.current &&
        modalContainerRef.current.nextElementSibling!.classList.contains(
          'hidden'
        ) &&
        target.classList.contains('modal-content')
      ) {
        modalContainerRef.current.children[0].classList.add('hide-modal');
        setTimeout(() => {
          dispatch(resetEditQuestion());
          switch (modalType) {
            case 'Add Issue':
              // case 'Add Patient Issue On Rounds':
              // case 'Add Employee Issue On Rounds':
              // case 'Add Support Issue On Rounds':
              logTrackingEvent(logTaskClick('issues', 'close'));
              break;
            case 'Add Feedback':
              // case 'Add Patient Feedback On Rounds':
              // case 'Add Employee Feedback On Rounds':
              // case 'Add Support Feedback On Rounds':
              logTrackingEvent(logTaskClick('feedback', 'close'));
              break;
            case 'Add Stoplight':
              logTrackingEvent(logTaskClick('stoplight', 'close'));
              break;
            default:
              // console.log('No type was selected');
              break;
          }
          dispatch(closeModal());
          if (storedDeepLinkTask) {
            navigate(
              modalData.roundingType === 'employee'
                ? '/employees/issues'
                : '/patients/issues'
            );
          }
        }, 300);
      }
    },
    [logTaskClick, logTrackingEvent, modalType]
  );

  useEffect(() => {
    if (modalState) {
      document.addEventListener('mousedown', closeFromOutside);
    }
    return () => document.removeEventListener('mousedown', closeFromOutside);
  }, [modalState]);

  let modalContent;

  switch (modalType) {
    case 'Add Issue':
    case 'Add Patient Issue On Rounds':
    case 'Add Employee Issue On Rounds':
    case 'Add Support Issue On Rounds':
      modalContent = <IssueModal />;
      break;
    case 'Edit Issue':
      modalContent = <IssueModal edit />;
      break;
    case 'Add Feedback':
    case 'Add Patient Feedback On Rounds':
    case 'Add Employee Feedback On Rounds':
    case 'Add Support Feedback On Rounds':
      modalContent = <FeedbackModal />;
      break;
    case 'Provisioning':
      modalContent = <AddProvisioningModal />;
      break;
    case 'Filter Issues':
      modalContent = <FilterModal filterType="issues" />;
      break;
    case 'Filter Feedback':
      modalContent = <FilterModal filterType="feedback" />;
      break;
    case 'Filter Stoplights':
      modalContent = <FilterModal filterType="stoplights" />;
      break;
    case 'Filter Admin Users':
      modalContent = <FilterModal filterType="adminUsers" />;
      break;
    case 'Filter Rounds Report':
      modalContent = <FilterModal filterType="roundsReport" />;
      break;
    case 'Filter Frequency Report':
      modalContent = <FilterModal filterType="frequencyReport" />;
      break;
    case 'Filter Census Report':
      modalContent = <FilterModal filterType="censusReport" />;
      break;
    case 'Filter Compliance Report':
      modalContent = <FilterModal filterType="complianceReport" />;
      break;
    case 'Filter Summary Report':
      modalContent = <FilterModal filterType="summaryReport" />;
      break;
    case 'Filter Employee Summary Report':
      modalContent = <FilterModal filterType="employeeSummaryReport" />;
      break;
    case 'Filter Subjects To Round List':
      modalContent = <FilterModal filterType="subjectsToRoundList" />;
      break;
    case 'Add Stoplight':
    case 'Edit Stoplight':
      modalContent = <StoplightModal />;
      break;
    case 'UnlistedPatientRound':
      modalContent = <UnlistedPatientRoundModal />;
      break;
    case 'UnlistedEmployeeRound':
      modalContent = <UnlistedEmployeeRoundModal />;
      break;
    case 'Add Delegates':
    case 'Add Employees':
      modalContent = <EmployeeRoundingSettingsModal />;
      break;
    case 'Edit User':
      modalContent = <UserModal />;
      break;
    case 'View Issue':
      modalContent = <IssueModalView />;
      break;
    case 'Add Question':
      modalContent = <AddQuestionModal />;
      break;
    default:
      // console.log('No type was selected');
      break;
  }

  return modalState ? (
    <ModalContainer
      id="Modal-Container"
      className="modal-content"
      ref={modalContainerRef}
    >
      {modalContent}
    </ModalContainer>
  ) : null;
};

const mapReduxStateToProps = (state: RootState) => ({
  modalData: state.ModalReducer.modalData,
  modalState: state.ModalReducer.modal,
  modalType: state.ModalReducer.modalType
});

export default connect(mapReduxStateToProps)(ModalPortal);
